import React, {
    useState,
    useEffect,
    useRef,
    useCallback,
    useMemo,
} from "react";
import { createPortal } from "react-dom";
import {
    X,
    MoreHorizontal,
    Maximize2,
    Minimize2,
    ChevronRight,
    ChevronLeft,
} from "lucide-react";
import { Button } from "@shadcn-in-nx/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@shadcn-in-nx/ui/dropdown-menu";
import { cn } from "@utils";

const ResizablePanels = ({
    children,
    layoutId = "default",
    defaultLayout = {},
    className = "",
    onLayoutChange,
    storageKey = "resizable-panels-layout",
}) => {
    // Initialize with equal widths if no default layout provided
    const getInitialLayout = () => {
        try {
            const saved = localStorage.getItem(`${storageKey}`);
            if (saved) {
                const parsedLayout = JSON.parse(saved);
                // Return saved layout for now, will normalize after mount
                return parsedLayout;
            }
        } catch (error) {
            console.warn("Failed to load saved layout:", error);
        }

        // Create equal width layout for all panels
        const childCount = React.Children.count(children);
        if (childCount === 0) return {};

        const equalWidth = 100 / childCount;
        const layout = {};

        React.Children.forEach(children, (child, index) => {
            const panelId = child.props?.panelId || `panel-${index}`;
            layout[panelId] = {
                width: defaultLayout[panelId]?.width || equalWidth,
                hidden: defaultLayout[panelId]?.hidden || false,
                collapsed: defaultLayout[panelId]?.collapsed || false,
            };
        });

        return layout;
    };

    const [layout, setLayout] = useState(() => getInitialLayout());
    const [maximizedPanel, setMaximizedPanel] = useState(null);

    const [isDragging, setIsDragging] = useState(false);
    const [dragInfo, setDragInfo] = useState(null);
    const [isInitialized, setIsInitialized] = useState(false);

    const containerRef = useRef(null);

    // Memoize children
    const memoizedChildren = useMemo(() => {
        return React.Children.toArray(children).map((child, index) => ({
            ...child,
            key: child.key || `panel-${index}`,
            panelId: child.props?.panelId || `panel-${index}`,
        }));
    }, [children]);

    // Get visible panels
    const visiblePanels = useMemo(() => {
        return memoizedChildren.filter(
            (child) => !layout[child.panelId]?.hidden
        );
    }, [memoizedChildren, layout]);

    // Helper function to normalize layout widths
    const normalizeLayoutWidths = useCallback(
        (inputLayout) => {
            const visible = memoizedChildren.filter(
                (child) => !inputLayout[child.panelId]?.hidden
            );

            if (visible.length === 0) return inputLayout;

            const newLayout = { ...inputLayout };

            // Convert any pixel values or "auto" to percentages
            let totalFixedWidth = 0;
            let autoCount = 0;

            visible.forEach((child) => {
                const width = newLayout[child.panelId]?.width;
                if (typeof width === "number" && width > 0 && width < 100) {
                    // Assume values less than 100 are percentages
                    // Keep as is
                } else if (typeof width === "number" && width >= 100) {
                    // Assume values >= 100 are pixels, convert to percentage
                    // For now, treat as fixed width that needs conversion
                    totalFixedWidth += width;
                } else if (width === "auto" || !width) {
                    autoCount++;
                }
            });

            // If we have mixed width types, normalize everything to percentages
            if (totalFixedWidth > 0 || autoCount > 0) {
                const equalWidth = 100 / visible.length;
                visible.forEach((child) => {
                    newLayout[child.panelId] = {
                        ...newLayout[child.panelId],
                        width: equalWidth,
                    };
                });
                return newLayout;
            }

            // Calculate total percentage width
            const totalWidth = visible.reduce((sum, child) => {
                const width = newLayout[child.panelId]?.width || 0;
                return sum + (typeof width === "number" ? width : 0);
            }, 0);

            if (totalWidth === 0 || Math.abs(totalWidth - 100) > 0.1) {
                // If all widths are 0 or don't sum to 100, redistribute equally
                const equalWidth = 100 / visible.length;
                visible.forEach((child) => {
                    newLayout[child.panelId] = {
                        ...newLayout[child.panelId],
                        width: equalWidth,
                    };
                });
            }

            return newLayout;
        },
        [memoizedChildren]
    );

    // Normalize widths to always sum to 100%
    const normalizeWidths = useCallback(
        (newLayout) => {
            const visible = memoizedChildren.filter(
                (child) => !newLayout[child.panelId]?.hidden
            );

            if (visible.length === 0) return newLayout;

            const totalWidth = visible.reduce((sum, child) => {
                return sum + (newLayout[child.panelId]?.width || 0);
            }, 0);

            if (totalWidth === 0) {
                // If all widths are 0, distribute equally
                const equalWidth = 100 / visible.length;
                visible.forEach((child) => {
                    newLayout[child.panelId] = {
                        ...newLayout[child.panelId],
                        width: equalWidth,
                    };
                });
            } else if (Math.abs(totalWidth - 100) > 0.1) {
                // Normalize to 100%
                const factor = 100 / totalWidth;
                visible.forEach((child) => {
                    newLayout[child.panelId] = {
                        ...newLayout[child.panelId],
                        width: (newLayout[child.panelId]?.width || 0) * factor,
                    };
                });
            }

            return newLayout;
        },
        [memoizedChildren]
    );

    // Save layout
    const saveLayout = useCallback(
        (newLayout) => {
            try {
                localStorage.setItem(
                    `${storageKey}-${layoutId}`,
                    JSON.stringify(newLayout)
                );
                onLayoutChange?.(newLayout);
            } catch (error) {
                console.warn("Failed to save layout:", error);
            }
        },
        [layoutId, storageKey, onLayoutChange]
    );

    // Reset layout
    const resetLayout = useCallback(() => {
        const childCount = React.Children.count(children);
        const equalWidth = 100 / childCount;
        const newLayout = {};

        React.Children.forEach(children, (child, index) => {
            const panelId = child.props?.panelId || `panel-${index}`;
            newLayout[panelId] = {
                width: equalWidth,
                hidden: false,
                collapsed: false,
            };
        });

        setLayout(newLayout);
        try {
            localStorage.removeItem(`${storageKey}-${layoutId}`);
        } catch (error) {
            console.warn("Failed to remove saved layout:", error);
        }
    }, [children, layoutId, storageKey]);

    // Toggle panel collapse
    const togglePanel = useCallback(
        (panelId) => {
            setLayout((prev) => {
                const newLayout = {
                    ...prev,
                    [panelId]: {
                        ...prev[panelId],
                        collapsed: !prev[panelId]?.collapsed,
                    },
                };
                return normalizeWidths(newLayout);
            });
        },
        [normalizeWidths]
    );

    // Toggle panel visibility
    const togglePanelVisibility = useCallback(
        (panelId) => {
            setLayout((prev) => {
                const newLayout = { ...prev };
                const wasHidden = newLayout[panelId]?.hidden;

                if (wasHidden) {
                    // Show panel - give it equal share with other visible panels
                    const currentVisible = memoizedChildren.filter(
                        (child) =>
                            child.panelId !== panelId &&
                            !newLayout[child.panelId]?.hidden
                    );
                    const newVisibleCount = currentVisible.length + 1;
                    const equalWidth = 100 / newVisibleCount;

                    // Redistribute all visible panels equally
                    [...currentVisible, { panelId }].forEach((panel) => {
                        const id = panel.panelId || panel;
                        newLayout[id] = {
                            ...newLayout[id],
                            width: equalWidth,
                            hidden: false,
                        };
                    });
                } else {
                    // Hide panel - redistribute its width among remaining visible panels
                    const panelWidth = newLayout[panelId]?.width || 0;
                    newLayout[panelId] = {
                        ...newLayout[panelId],
                        hidden: true,
                    };

                    const remainingVisible = memoizedChildren.filter(
                        (child) =>
                            child.panelId !== panelId &&
                            !newLayout[child.panelId]?.hidden
                    );

                    if (remainingVisible.length > 0 && panelWidth > 0) {
                        const redistributeWidth =
                            panelWidth / remainingVisible.length;
                        remainingVisible.forEach((child) => {
                            newLayout[child.panelId] = {
                                ...newLayout[child.panelId],
                                width:
                                    (newLayout[child.panelId]?.width || 0) +
                                    redistributeWidth,
                            };
                        });
                    }
                }

                return normalizeWidths(newLayout);
            });
        },
        [memoizedChildren, normalizeWidths]
    );

    // Maximize panel
    const maximizePanel = useCallback((panelId) => {
        setMaximizedPanel(panelId);
    }, []);

    // Restore panel from maximized state
    const restorePanel = useCallback(() => {
        setMaximizedPanel(null);
    }, []);

    // Start dragging
    const startDrag = useCallback(
        (e, leftPanelId, rightPanelId) => {
            e.preventDefault();
            e.stopPropagation();

            // Use setPointerCapture if available for better tracking
            if (e.target && e.target.setPointerCapture) {
                e.target.setPointerCapture(e.pointerId || 1);
            }

            setIsDragging(true);
            setDragInfo({
                leftPanelId,
                rightPanelId,
                startX: e.clientX,
                initialLeftWidth: layout[leftPanelId]?.width || 0,
                initialRightWidth: layout[rightPanelId]?.width || 0,
            });

            document.body.style.cursor = "col-resize";
            document.body.style.userSelect = "none";
            document.body.style.pointerEvents = "auto";
        },
        [layout]
    );

    // Handle drag movement
    const handleDrag = useCallback(
        (e) => {
            if (!isDragging || !dragInfo || !containerRef.current) return;

            e.preventDefault();
            e.stopPropagation();

            const containerRect = containerRef.current.getBoundingClientRect();
            const containerWidth = containerRect.width;
            const deltaX = e.clientX - dragInfo.startX;
            const deltaPercent = (deltaX / containerWidth) * 100;

            setLayout((prev) => {
                const newLayout = { ...prev };
                const leftPanel = dragInfo.leftPanelId;
                const rightPanel = dragInfo.rightPanelId;

                // Use initial widths instead of current widths
                const initialLeftWidth = dragInfo.initialLeftWidth;
                const initialRightWidth = dragInfo.initialRightWidth;
                const totalAvailable = initialLeftWidth + initialRightWidth;

                // Calculate new widths with constraints
                const minWidth = 1; // Minimum 5% per panel before collapse
                const maxWidth = totalAvailable;

                let newLeftWidth = initialLeftWidth + deltaPercent;
                let newRightWidth = initialRightWidth - deltaPercent;

                // Check for collapse
                if (newLeftWidth < minWidth) {
                    // Collapse left panel
                    newLeftWidth = 0;
                    newRightWidth = totalAvailable;
                    newLayout[leftPanel].hidden = true;
                } else if (newRightWidth < minWidth) {
                    // Collapse right panel
                    newRightWidth = 0;
                    newLeftWidth = totalAvailable;
                    newLayout[rightPanel].hidden = true;
                } else {
                    // Normal resize
                    newLayout[leftPanel].hidden = false;
                    newLayout[rightPanel].hidden = false;

                    // Clamp to valid ranges
                    newLeftWidth = Math.max(
                        minWidth,
                        Math.min(maxWidth, newLeftWidth)
                    );
                    newRightWidth = Math.max(
                        minWidth,
                        Math.min(maxWidth, newRightWidth)
                    );

                    // Ensure total equals the available width (usually 100%)
                    const currentTotal = newLeftWidth + newRightWidth;
                    if (Math.abs(currentTotal - totalAvailable) > 0.01) {
                        // Adjust proportionally to maintain total
                        const adjustment = totalAvailable - currentTotal;
                        // Distribute adjustment between panels
                        newLeftWidth += adjustment / 2;
                        newRightWidth += adjustment / 2;

                        // Re-clamp after adjustment
                        newLeftWidth = Math.max(
                            minWidth,
                            Math.min(maxWidth, newLeftWidth)
                        );
                        newRightWidth = Math.max(
                            minWidth,
                            Math.min(maxWidth, newRightWidth)
                        );

                        // Final normalization to ensure exact total
                        const finalTotal = newLeftWidth + newRightWidth;
                        if (Math.abs(finalTotal - totalAvailable) > 0.01) {
                            const factor = totalAvailable / finalTotal;
                            newLeftWidth *= factor;
                            newRightWidth *= factor;
                        }
                    }
                }

                newLayout[leftPanel] = {
                    ...newLayout[leftPanel],
                    width: newLeftWidth,
                };
                newLayout[rightPanel] = {
                    ...newLayout[rightPanel],
                    width: newRightWidth,
                };

                return newLayout;
            });
        },
        [isDragging, dragInfo]
    );

    // Stop dragging
    const stopDrag = useCallback((e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
            // Release pointer capture if used
            if (e.target && e.target.releasePointerCapture) {
                try {
                    e.target.releasePointerCapture(e.pointerId || 1);
                } catch (err) {
                    // Ignore errors if pointer wasn't captured
                }
            }
        }
        setIsDragging(false);
        setDragInfo(null);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
        document.body.style.pointerEvents = "";
    }, []);

    // Event listeners for drag
    useEffect(() => {
        if (isDragging) {
            // Use capture phase to catch events before they bubble
            const options = { capture: true, passive: false };
            document.addEventListener("mousemove", handleDrag, options);
            document.addEventListener("mouseup", stopDrag, options);
            // Also listen for mouseleave in case mouse leaves window
            document.addEventListener("mouseleave", stopDrag, options);

            return () => {
                document.removeEventListener("mousemove", handleDrag, options);
                document.removeEventListener("mouseup", stopDrag, options);
                document.removeEventListener("mouseleave", stopDrag, options);
            };
        }
    }, [isDragging, handleDrag, stopDrag]);

    // Save layout when it changes
    useEffect(() => {
        if (!isDragging && layout) {
            const timeoutId = setTimeout(() => {
                saveLayout(layout);
            }, 100);

            return () => clearTimeout(timeoutId);
        }
    }, [layout, isDragging, saveLayout]);

    // Initialize layout after component mounts
    useEffect(() => {
        if (!isInitialized && memoizedChildren.length > 0) {
            // Small delay to allow the DOM to settle
            const timeoutId = setTimeout(() => {
                setLayout((prevLayout) => {
                    const normalizedLayout = normalizeLayoutWidths(prevLayout);
                    setIsInitialized(true);
                    return normalizedLayout;
                });
            }, 10);

            return () => clearTimeout(timeoutId);
        }
    }, [isInitialized, memoizedChildren, normalizeLayoutWidths]);

    // Render panel
    const renderPanel = (child, index) => {
        const panelId = child.panelId;
        const panelLayout = layout[panelId] || {};
        const isHidden = panelLayout.hidden;
        const isMaximized = maximizedPanel === panelId;
        const isAnyMaximized = maximizedPanel !== null;

        // If another panel is maximized and this isn't it, don't render
        if (isAnyMaximized && !isMaximized) return null;

        if (isHidden) return null;

        const width = isMaximized ? 100 : panelLayout.width || 0;
        const isLast = index === visiblePanels.length - 1;

        // Get hidden panels for this panel's dropdown
        const hiddenPanels = memoizedChildren.filter(
            (hiddenChild) => layout[hiddenChild.panelId]?.hidden
        );

        return (
            <React.Fragment key={panelId}>
                <div
                    className={cn(
                        "relative flex flex-col",
                        isMaximized ? "h-full w-full" : "",
                        child.props.className
                    )}
                    style={{
                        width: `${width}%`,
                        minWidth: 0,
                        flexShrink: 0,
                        flexGrow: 0,
                        flexBasis: `${width}%`,
                        height: isMaximized ? "100%" : "auto",
                    }}
                >
                    {/* Panel Header */}
                    {child.props.title && (
                        <div className="flex items-center justify-between p-2 bg-muted/50 flex-shrink-0">
                            <div className="flex items-center gap-2">
                                <span className="text-sm font-medium">
                                    {child.props.title}
                                </span>
                                {isMaximized && (
                                    <span className="text-xs text-muted-foreground">
                                        (Maximized)
                                    </span>
                                )}
                            </div>
                            <div className="flex items-center gap-1">
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-6 w-6 p-0"
                                        >
                                            <MoreHorizontal className="h-4 w-4" />
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                        <DropdownMenuItem onClick={resetLayout}>
                                            Reset Layout
                                        </DropdownMenuItem>
                                        {hiddenPanels.length > 0 && (
                                            <>
                                                <div className="border-t my-1" />
                                                {hiddenPanels.map(
                                                    (hiddenChild) => (
                                                        <DropdownMenuItem
                                                            key={
                                                                hiddenChild.panelId
                                                            }
                                                            onClick={() =>
                                                                togglePanelVisibility(
                                                                    hiddenChild.panelId
                                                                )
                                                            }
                                                        >
                                                            Show{" "}
                                                            {hiddenChild.props
                                                                .title ||
                                                                hiddenChild.panelId}
                                                        </DropdownMenuItem>
                                                    )
                                                )}
                                            </>
                                        )}
                                    </DropdownMenuContent>
                                </DropdownMenu>
                                {isMaximized ? (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 w-6 p-0 hover:bg-blue-100 hover:text-blue-600"
                                        onClick={restorePanel}
                                        title="Restore panel"
                                    >
                                        <Minimize2 className="h-4 w-4" />
                                    </Button>
                                ) : (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 w-6 p-0 hover:bg-blue-100 hover:text-blue-600"
                                        onClick={() => maximizePanel(panelId)}
                                        title="Maximize panel"
                                    >
                                        <Maximize2 className="h-4 w-4" />
                                    </Button>
                                )}

                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 w-6 p-0 hover:bg-red-100 hover:text-red-600"
                                    onClick={() =>
                                        togglePanelVisibility(panelId)
                                    }
                                    title="Close panel"
                                >
                                    <X className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    )}

                    {/* Panel Content */}
                    <div className="flex-1 overflow-y-auto overflow-x-hidden min-h-0 min-w-0">
                        {child}
                    </div>
                </div>

                {/* Separator and Drag Handle - only show if not maximized */}
                {!isLast && !isMaximized && (
                    <div className="relative flex-shrink-0">
                        {/* Separator Line */}
                        <div className="w-px h-full "></div>

                        {/* Drag Handle */}
                        {child.props.resizable !== false && (
                            <div
                                className="absolute top-0 h-full hover:bg-blue-500/50 cursor-col-resize z-10 flex items-center justify-center"
                                onMouseDown={(e) => {
                                    const nextVisibleIndex = index + 1;
                                    if (
                                        nextVisibleIndex < visiblePanels.length
                                    ) {
                                        startDrag(
                                            e,
                                            panelId,
                                            visiblePanels[nextVisibleIndex]
                                                .panelId
                                        );
                                    }
                                }}
                                style={{ left: "-3px", width: "10px" }}
                            >
                                <div className="w-1 h-8 bg-gray-300 rounded opacity-0 hover:opacity-100 transition-opacity" />
                            </div>
                        )}
                    </div>
                )}
            </React.Fragment>
        );
    };

    // Render reopen triggers
    const renderReopenTriggers = () => {
        const hiddenPanels = memoizedChildren.filter(
            (child) => layout[child.panelId]?.hidden
        );

        if (hiddenPanels.length === 0) return null;

        return hiddenPanels.map((child) => {
            const isFirst =
                memoizedChildren.findIndex(
                    (c) => c.panelId === child.panelId
                ) === 0;
            const isLast =
                memoizedChildren.findIndex(
                    (c) => c.panelId === child.panelId
                ) ===
                memoizedChildren.length - 1;

            if (isFirst) {
                return (
                    <div
                        key={`reopen-${child.panelId}`}
                        className="absolute left-0 top-1/2 -translate-y-1/2 z-50"
                    >
                        <Button
                            variant="secondary"
                            size="sm"
                            className="h-12 w-6 p-0 rounded-l-none rounded-r-lg shadow-md border-l-0 bg-gray-50 hover:bg-gray-100"
                            onClick={() => togglePanelVisibility(child.panelId)}
                            title={`Reopen ${child.props.title || "panel"}`}
                        >
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                    </div>
                );
            }

            if (isLast) {
                return (
                    <div
                        key={`reopen-${child.panelId}`}
                        className="absolute right-0 top-1/2 -translate-y-1/2 z-50"
                    >
                        <Button
                            variant="secondary"
                            size="sm"
                            className="h-12 w-6 p-0 rounded-r-none rounded-l-lg shadow-md border-r-0 bg-gray-50 hover:bg-gray-100"
                            onClick={() => togglePanelVisibility(child.panelId)}
                            title={`Reopen ${child.props.title || "panel"}`}
                        >
                            <ChevronLeft className="h-4 w-4" />
                        </Button>
                    </div>
                );
            }

            return null;
        });
    };

    return (
        <div
            ref={containerRef}
            className={cn(
                "relative flex flex-row h-full w-full overflow-hidden",
                className
            )}
            style={{ minWidth: 0, maxWidth: "100%" }}
        >
            {visiblePanels.map((child, index) => renderPanel(child, index))}
            {renderReopenTriggers()}

            {/* Drag overlay - captures all mouse events during drag */}
            {isDragging &&
                createPortal(
                    <div
                        className="fixed inset-0 z-50"
                        style={{ cursor: "col-resize", pointerEvents: "auto" }}
                        onMouseMove={handleDrag}
                        onMouseUp={stopDrag}
                    />,
                    document.body
                )}
        </div>
    );
};

// Panel component
const Panel = ({
    children,
    panelId,
    title,
    resizable = true,
    className = "",
}) => {
    return <div className={cn("h-full w-full min-w-0 overflow-hidden", className)}>{children}</div>;
};

export { ResizablePanels, Panel };
